import { Board } from 'chessboard/model/board/types';
import { StateMachine } from 'chessboard/core/state/config/types';
import { Core } from 'chessboard/core/config/types';
import { ChessError } from 'chessboard/model/error';
import { ErrorCodes } from 'chessboard/model/error/types';
import type { Markings } from 'chessboard/plugins/markings/config/types';
import { HighlightType } from 'chessboard/plugins/highlights/config/types';
import { EffectType } from 'chessboard/plugins/effects/config/types';
import { ArrowType } from 'chessboard/plugins/arrows/config/types';

export const idle = {
  /**
   * This event signals the user's intent to try to select a piece (Click method)
   * or pick up a piece (Drag method).
   */
  [Core.UserEvents.PointerdownLeft]: ({
    context,
    data: { coords, square },
    UpdateParams,
  }: StateMachine.TransitionParams) => {
    const {
      api: { getPieces, markings },
      mode: { getOptions },
      options,
      queue,
      renderer,
      state,
    } = context;

    const pieceOnSquare = getPieces().getCollection()[square];
    const isSetupMode = context.api.getMode().name === 'setup';
    const setupImprovements = context.api.getOptions().setupImprovements;

    if (setupImprovements && isSetupMode && pieceOnSquare) {
      context.api.setup.removePiece(square);

      return StateMachine.States.Idle;
    }

    // Only clear markings if left-clicking on the board. This ensures that markings
    // stay if, for instance, we're clicking on the move list to go to a different node.
    if (square && !options.disableUserMarkings) {
      queue.add((<Markings.API>markings).removeAllWhere, {
        node: true,
        persistent: false,
        types: [ArrowType, EffectType, HighlightType],
      });
    }

    if (!options.enabled || !getOptions().canInteractWithPieces) {
      return StateMachine.States.Idle;
    }

    const pieces = getPieces();

    // The square is empty, don't do anything.
    if (!pieces.isDefined(square)) {
      return StateMachine.States.Idle;
    }

    // In Click mode, we just want to select the piece, not pick it up.
    if (options.moveMethod === Board.MoveMethods.Click) {
      const squareCoords = renderer?.getCoordsFromSquare(square, options.flipped);

      if (typeof squareCoords === 'undefined') {
        throw new ChessError({
          code: ErrorCodes.BadData,
          message: `getCoordsFromSquare returned undefined for ${square}`,
        });
      }

      // In Drag mode, select the square and pick up the piece
      UpdateParams.dragPiece(squareCoords, square);

      state.set({ hideHoverSquare: true });

      renderer?.dragPiece({ coords: squareCoords, square }, context);

      return StateMachine.States.PieceDragging;
    }

    // In Drag mode, select the square and pick up the piece
    UpdateParams.dragPiece(coords, square);

    const squareCoords = coords || renderer?.getCoordsFromSquare(square, options.flipped);
    renderer?.dragPiece({ coords: squareCoords, square }, context);

    return StateMachine.States.PieceDragging;
  },

  [Core.UserEvents.PointerdownRight]: ({
    context,
    data: { square },
    UpdateParams,
  }: StateMachine.TransitionParams) => {
    const { options } = context;

    if (!options.enabled || options.disableUserMarkings) {
      return;
    }

    UpdateParams.setMarkingStartSquare(square);

    return StateMachine.States.AddingMarkings;
  },
};
